import React, { Fragment, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { TextLink } from 'secondstep-components'
import { domains } from 'config/env'
import { getLessonCompletionDisplayPercent } from 'utils/reportsHelpers'
import AdminGradeList from 'components/AdminGradeList'
import AdminBHULessonProgressCard from 'components/AdminBHULessonProgressCard'
import AdminNoClasses from 'components/AdminNoClasses'
import PlaybookSteps from 'components/admin/PlaybookSteps'
import {
  DIGITAL_WELL_BEING_UNIT_LABEL,
  LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL,
} from 'components/AdminSiteHeader/constants'
import {
  getK5DigitalFromPreferences,
  getMspDigitalFromPreferences,
} from 'utils/sitePreferenceHelpers'
import { isFlagOn } from 'utils/featureFlags'
import { TOOLTIP_LOCATIONS, TOOLTIP_PROGRAMS } from 'utils/tooltipHelpers'
import {
  CLASSES_NOT_STARTED_MESSAGE,
  DWB_LESSONS_COMPLETED_LABEL,
  NOT_STARTED_MESSAGE,
  TRAINING_PROGRESS_REPORT_LABEL,
} from './constants'
import {
  BodyWrapper,
  ClassesProgressWrapper,
  ClassProgressBullet,
  ClassesNotStartedText,
  ClassesNotStartedWrapper,
  ClassesStartedText,
  CardHeading,
  DwbCardSummary,
  DwbClassesCountText,
  DwbLessonsCompletedText,
  DwbLessonsTooltip,
  DwbSummaryLine,
  GoalTextClasses,
  LessonCompletionWrapper,
  NoClassesMessage,
  PanelWrapper,
  PipeSeparator,
  SiteCollapsible,
  StyledAdminLessonProgressTabs,
  StyledHairline,
  StyledHeader,
  StyledHeaderProgressBar,
  VerticalDivider,
} from './component.styles'

const formatClassCountLabel = count =>
  `${count} Class${count === 1 ? '' : 'es'}`

const formatClassesNotMarkedDoneLabel = count => {
  const noun = count === 1 ? 'class' : 'classes'
  const verb = count === 1 ? "hasn't" : "haven't"
  return `${count} ${noun} ${verb} marked a lesson done yet`
}

const LessonCompletionAccordion = ({
  activeIndex,
  classesCreated,
  classesNotStarted,
  grades,
  handleAccordionClick,
  index,
  lessonCompletionUnitsLabel,
  name,
  siteId,
  preferences,
  summary,
}) => {
  const lessonCompletionBarMounted = useRef(true)
  useEffect(
    () => () => {
      lessonCompletionBarMounted.current = false
    },
    [],
  )

  const lessonCompletionPercentage = getLessonCompletionDisplayPercent(summary)
  const isSpecializedUnits =
    Boolean(lessonCompletionUnitsLabel) &&
    lessonCompletionUnitsLabel === LESSON_COMPLETION_SPECIALIZED_UNITS_LABEL
  const k5Digital = getK5DigitalFromPreferences(preferences)
  const mspDigital = getMspDigitalFromPreferences(preferences)
  const hasBHUEnabled = mspDigital?.bullyingHarassmentUnitEnabled ?? false
  const hasBHUClassCreated = summary?.unit5?.classesCreated > 0
  const bhuStatus = !isSpecializedUnits && hasBHUEnabled && hasBHUClassCreated
  const hasNoDwbSummaryData =
    !summary ||
    ((summary.completedLessons ?? 0) === 0 &&
      (summary.totalLessons ?? 0) === 0 &&
      (summary.completionPercentage ?? 0) === 0)
  const showEmptyDwbReport =
    isSpecializedUnits &&
    (!isFlagOn(['feature_LEARN-25816-dwb-reporting']) || hasNoDwbSummaryData)
  const trainingReportHref = `${domains.ADMIN_DOMAIN}/training-report/digital-well-being/${siteId}`

  return (
    <SiteCollapsible
      accordionPanelDataTestId={`school-row`}
      ariaLabel={`${name}-accordion`}
      classesNotStarted={classesNotStarted}
      dataTestId={`site-collapsible-${siteId}`}
      followActiveProp={true}
      isActive={activeIndex === index}
      key={siteId}
      onClick={() => handleAccordionClick(index)}
      title={
        <PanelWrapper>
          <StyledHeader classesNotStarted={classesNotStarted}>
            {name}
          </StyledHeader>
          <ClassesProgressWrapper classesNotStarted={classesNotStarted}>
            {classesCreated === 0 && (
              <NoClassesMessage>{CLASSES_NOT_STARTED_MESSAGE}</NoClassesMessage>
            )}
            {classesCreated > 0 && (
              <Fragment key="classes-created-key">
                <Fragment key="classes-not-started-key">
                  <Fragment key="classes-created-goal-key">
                    <ClassesStartedText>{classesCreated}</ClassesStartedText>
                    {!isSpecializedUnits && (
                      <Fragment key="classes-creation-goal">
                        <PipeSeparator>|</PipeSeparator>
                        <GoalTextClasses>
                          {`Goal: 
                      ${
                        k5Digital?.classCreationGoalValue
                          ? k5Digital.classCreationGoalValue
                          : 'Not Set'
                      }`}
                        </GoalTextClasses>
                      </Fragment>
                    )}
                  </Fragment>
                  {classesNotStarted > 0 && (
                    <ClassesNotStartedWrapper>
                      <ClassProgressBullet />
                      <ClassesNotStartedText>
                        {classesNotStarted} {NOT_STARTED_MESSAGE}
                      </ClassesNotStartedText>
                    </ClassesNotStartedWrapper>
                  )}
                </Fragment>
              </Fragment>
            )}
          </ClassesProgressWrapper>
          <LessonCompletionWrapper
            classesCreated={classesCreated}
            isDwb={isSpecializedUnits}
          >
            <StyledHeaderProgressBar
              $fillPercent={lessonCompletionPercentage}
              $isDwb={isSpecializedUnits}
              isParentMounted={lessonCompletionBarMounted}
              keyText={`lesson-completion-header-${siteId}`}
              percentComplete={lessonCompletionPercentage}
              shouldRenderIndicator={classesCreated > 0}
              shouldRenderPercentage
            />
            {!isSpecializedUnits && (
              <Fragment key="lesson-completion-goal">
                <PipeSeparator>|</PipeSeparator>
                <GoalTextClasses>{`Goal: 
              ${
                k5Digital?.lessonCompletionGoalValue
                  ? `${k5Digital.lessonCompletionGoalValue}%`
                  : 'Not Set'
              }`}</GoalTextClasses>
              </Fragment>
            )}
          </LessonCompletionWrapper>
        </PanelWrapper>
      }
    >
      {!isSpecializedUnits && (
        <PlaybookSteps
          key={`monitor-${siteId}`}
          preferences={preferences}
          showMonitorViewOnly={true}
          siteId={siteId}
        />
      )}
      <BodyWrapper
        classesCreated={classesCreated}
        isDwbEmptyReport={showEmptyDwbReport}
      >
        {showEmptyDwbReport ? (
          <AdminNoClasses isDwbEmptyReport />
        ) : isSpecializedUnits ? (
          <Fragment key="specialized-body-wrapper">
            {classesCreated === 0 && <AdminNoClasses />}
            {classesCreated > 0 && (
              <Fragment key="specialized-classes-created-body">
                <CardHeading>{DIGITAL_WELL_BEING_UNIT_LABEL}</CardHeading>
                <DwbCardSummary data-testid="dwb-card-summary">
                  <DwbSummaryLine>
                    <DwbClassesCountText>
                      {formatClassCountLabel(classesCreated)}
                    </DwbClassesCountText>
                    {classesNotStarted > 0 && (
                      <Fragment key="dwb-classes-not-marked">
                        <VerticalDivider aria-hidden="true" />
                        <ClassesNotStartedWrapper>
                          <ClassProgressBullet />
                          <ClassesNotStartedText>
                            {formatClassesNotMarkedDoneLabel(classesNotStarted)}
                          </ClassesNotStartedText>
                        </ClassesNotStartedWrapper>
                      </Fragment>
                    )}
                  </DwbSummaryLine>
                  <DwbSummaryLine>
                    <DwbLessonsCompletedText>
                      {`${lessonCompletionPercentage}%${DWB_LESSONS_COMPLETED_LABEL}`}
                    </DwbLessonsCompletedText>
                    <DwbLessonsTooltip
                      data-testid="dwb-lessons-completed-tooltip"
                      iconClass="fa-question-circle"
                      location={TOOLTIP_LOCATIONS.TABLE_LESSONS}
                      monitorView
                      programKey={TOOLTIP_PROGRAMS.K8_MONITOR}
                    />
                    <VerticalDivider aria-hidden="true" />
                    <TextLink
                      aria-label="View training progress report"
                      dataTestId="dwb-training-progress-report-link"
                      href={trainingReportHref}
                      target="_self"
                    >
                      {TRAINING_PROGRESS_REPORT_LABEL}
                    </TextLink>
                  </DwbSummaryLine>
                </DwbCardSummary>
                <StyledHairline />
                <AdminGradeList
                  dwbProgressBars
                  grades={grades}
                  siteId={siteId}
                  splitTwoColumns
                />
              </Fragment>
            )}
          </Fragment>
        ) : (
          <Fragment key="body-wrapper">
            {classesCreated === 0 && <AdminNoClasses />}
            {classesCreated > 0 && (
              <Fragment key="classes-created-body">
                <CardHeading>Core Curriculum (Units 1-4)</CardHeading>
                <StyledAdminLessonProgressTabs bhuStatus={bhuStatus} />
                <StyledHairline />
                <AdminGradeList grades={grades} siteId={siteId} />
              </Fragment>
            )}
          </Fragment>
        )}
      </BodyWrapper>
      {bhuStatus && (
        <AdminBHULessonProgressCard
          classesCreated={classesCreated}
          grades={grades}
          siteId={siteId}
          summary={summary}
        />
      )}
    </SiteCollapsible>
  )
}

LessonCompletionAccordion.propTypes = {
  activeIndex: PropTypes.number,
  classesCreated: PropTypes.number,
  classesNotStarted: PropTypes.number,
  grades: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  handleAccordionClick: PropTypes.func,
  index: PropTypes.number,
  lessonCompletionUnitsLabel: PropTypes.string,
  name: PropTypes.string,
  preferences: PropTypes.array,
  siteId: PropTypes.number,
  summary: PropTypes.object,
}

export default LessonCompletionAccordion
